import { useState, useEffect } from 'react';

export default function CompromissoTimeline() {
  const [nome, setNome] = useState('');
  const [periodo, setPeriodo] = useState(21);
  const [assinado, setAssinado] = useState(false);
  const [diasPraticados, setDiasPraticados] = useState<number[]>([]);

  useEffect(() => {
    const saved = localStorage.getItem('timeline-compromisso');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setNome(data.nome || '');
        setPeriodo(data.periodo || 21);
        setAssinado(data.assinado || false);
        setDiasPraticados(data.diasPraticados || []);
      } catch (e) {
        console.error('Erro ao carregar compromisso');
      }
    }
  }, []);

  const salvarCompromisso = () => {
    const data = { nome, periodo, assinado, diasPraticados };
    localStorage.setItem('timeline-compromisso', JSON.stringify(data));
  };

  const assinarCompromisso = () => {
    if (nome.trim()) {
      setAssinado(true);
      salvarCompromisso();
    }
  };

  const toggleDia = (dia: number) => {
    const novoDias = diasPraticados.includes(dia)
      ? diasPraticados.filter(d => d !== dia)
      : [...diasPraticados, dia];
    setDiasPraticados(novoDias);
    setTimeout(() => {
      const data = { nome, periodo, assinado, diasPraticados: novoDias };
      localStorage.setItem('timeline-compromisso', JSON.stringify(data));
    }, 100);
  };

  const calcularSequencia = () => {
    if (diasPraticados.length === 0) return 0;
    const sorted = [...diasPraticados].sort((a, b) => a - b);
    let maxSequencia = 1;
    let sequenciaAtual = 1;
    
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] === sorted[i - 1] + 1) {
        sequenciaAtual++;
        maxSequencia = Math.max(maxSequencia, sequenciaAtual);
      } else {
        sequenciaAtual = 1;
      }
    }
    return maxSequencia;
  };

  const progresso = Math.round((diasPraticados.length / periodo) * 100);
  const sequencia = calcularSequencia();

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Compromisso de Prática
          </h2>
          <p className="text-xl text-gray-600">
            Assine seu compromisso e acompanhe sua jornada de transformação
          </p>
        </div>

        {!assinado ? (
          <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 rounded-3xl p-8 shadow-xl border-2 border-violet-200">
            <div className="bg-white rounded-2xl p-8 mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Meu Compromisso Pessoal
              </h3>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">
                    Seu Nome Completo
                  </label>
                  <input
                    type="text"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    placeholder="Digite seu nome..."
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-violet-500 focus:ring-2 focus:ring-violet-200 transition-all text-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">
                    Período de Prática
                  </label>
                  <select
                    value={periodo}
                    onChange={(e) => setPeriodo(parseInt(e.target.value))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-violet-500 focus:ring-2 focus:ring-violet-200 transition-all text-gray-900"
                  >
                    <option value={7}>7 dias</option>
                    <option value={14}>14 dias</option>
                    <option value={21}>21 dias (recomendado)</option>
                    <option value={30}>30 dias</option>
                    <option value={60}>60 dias</option>
                    <option value={90}>90 dias</option>
                  </select>
                </div>

                <div className="bg-violet-50 border-l-4 border-violet-600 p-6 rounded-r-xl">
                  <p className="text-gray-700 leading-relaxed italic">
                    "Eu, <strong>{nome || '[Seu Nome]'}</strong>, comprometo-me a praticar a Técnica da Linha do Tempo 
                    por <strong>{periodo} dias consecutivos</strong>, liberando emoções negativas do passado e criando 
                    um futuro motivador. Reconheço que essa prática transformará minha relação com o tempo e minhas memórias."
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={assinarCompromisso}
              disabled={!nome.trim()}
              className={`w-full py-4 rounded-xl font-bold text-lg transition-all whitespace-nowrap ${
                nome.trim()
                  ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:shadow-xl hover:scale-105 cursor-pointer'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Assinar Compromisso
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 rounded-3xl p-8 shadow-xl border-2 border-violet-200">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                <h3 className="text-2xl font-bold text-gray-900">
                  Olá, {nome}! 👋
                </h3>
                <button
                  onClick={() => {
                    setAssinado(false);
                    setDiasPraticados([]);
                    localStorage.removeItem('timeline-compromisso');
                  }}
                  className="text-sm text-violet-600 hover:text-violet-700 font-semibold whitespace-nowrap"
                >
                  Resetar Compromisso
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
                  <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <i className="ri-calendar-check-line text-white text-2xl"></i>
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">
                    {diasPraticados.length}
                  </div>
                  <div className="text-sm text-gray-600">Dias Praticados</div>
                </div>

                <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
                  <div className="w-12 h-12 bg-gradient-to-br from-fuchsia-500 to-pink-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <i className="ri-fire-line text-white text-2xl"></i>
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">
                    {sequencia}
                  </div>
                  <div className="text-sm text-gray-600">Maior Sequência</div>
                </div>

                <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <i className="ri-trophy-line text-white text-2xl"></i>
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">
                    {progresso}%
                  </div>
                  <div className="text-sm text-gray-600">Progresso</div>
                </div>
              </div>

              {sequencia >= 7 && (
                <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-l-4 border-amber-500 p-4 rounded-r-xl mb-6">
                  <div className="flex items-center gap-3">
                    <i className="ri-fire-fill text-amber-600 text-2xl"></i>
                    <p className="text-sm text-gray-700">
                      <strong>Incrível!</strong> Você está em uma sequência de {sequencia} dias! Continue assim! 🔥
                    </p>
                  </div>
                </div>
              )}

              {progresso === 100 && (
                <div className="bg-gradient-to-r from-emerald-50 to-green-50 border-l-4 border-emerald-500 p-6 rounded-r-xl mb-6">
                  <div className="flex items-center gap-4">
                    <i className="ri-trophy-fill text-emerald-600 text-4xl"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Parabéns! Você completou o desafio! 🎉</h4>
                      <p className="text-sm text-gray-700">
                        Você praticou a Técnica da Linha do Tempo por {periodo} dias. Sua linha do tempo está transformada!
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h4 className="font-bold text-gray-900 mb-4">
                  Marque os dias que você praticou:
                </h4>
                <div className="grid grid-cols-7 gap-2">
                  {Array.from({ length: periodo }, (_, i) => i + 1).map((dia) => (
                    <button
                      key={dia}
                      onClick={() => toggleDia(dia)}
                      className={`aspect-square rounded-xl font-semibold text-sm transition-all hover:scale-110 cursor-pointer ${
                        diasPraticados.includes(dia)
                          ? 'bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {dia}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
